<template>
  <div class="story-section">
    <div class="story-container">
      <div class="story-header">
        <div class="story-badge">Our Story</div>
        <h2 class="story-title">
          Rethinking Asia's Data Story -<br class="responsive-br" />
          With Speed, Scale & Intelligence
        </h2>
      </div>

      <div class="carousel-container" >
        <div
          class="cards-wrapper"
          :style="{ transform: `translateX(-${activeCardIndex * 100}%)` }"
        >
          <div
            v-for="(card, index) in cards"
            :key="index"
            class="carousel-card"
            :class="{ active: activeCardIndex === index }"
            ref="carousel"
          >
            <div class="card-content">
              <div class="card-left">
                <img :src="card.icon" alt="Icon" class="card-icon" />
                <p class="card-text">{{ card.text }}</p>
              </div>
              <div class="card-right">
                <img
                  :src="card.image"
                  alt="Illustration"
                  class="card-illustration"
                />
              </div>
            </div>
          </div>
        </div>

        <div class="carousel-indicators">
          <div
            v-for="(_, index) in cards"
            :key="index"
            class="indicator"
            :class="{ active: activeCardIndex === index }"
            @click="activeCardIndex = index"
          ></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import config from "@/config";

export default {
  name: "StoryCarousel",
  data() {
    return {
      activeCardIndex: 0,
      autoPlayInterval: null,
      cards: [
        {
          icon: this.getCDNlink(
            "assets/new-assets/About-Us/International_Banking.gif"
          ),
          text: "Our journey didn't begin with data — it began deep within the world of international banking and trade finance, where our core team worked closely with global banks & MNCs, navigating the real-world complexities of cross-border trade.",
          image: this.getCDNlink(
            "assets/new-assets/About-Us/International_Banking.webp"
          ),
        },
        {
          icon: this.getCDNlink("assets/new-assets/About-Us/Trade_Finance.gif"),
          text: "We discovered a critical gap in the market: businesses needed reliable, real-time data about their international counterparties, but existing solutions were slow, outdated, and often irrelevant.",
          image: this.getCDNlink(
            "assets/new-assets/About-Us/Trade_Finance.webp"
          ),
        },
        {
          icon: this.getCDNlink("assets/new-assets/About-Us/AI_Powered.gif"),
          text: "This insight led us to create Credence — a platform that combines cutting-edge technology with deep domain expertise to deliver actionable intelligence for international trade and risk assessment.",
          image: this.getCDNlink("assets/new-assets/About-Us/AI_Powered.webp"),
        },
      ],
    };
  },
  mounted() {
    this.startAutoPlay();
    const carousel = this.$refs.carousel;
    carousel.forEach(card => {
    card.addEventListener("mouseover", this.pauseAutoPlay);
    card.addEventListener("mouseleave", this.startAutoPlay);
  });
  },
  beforeUnmount() {
    this.pauseAutoPlay();
    const carousel = this.$refs.carousel;
    if (carousel) {
      carousel.forEach(card => {
    card.removeEventListener("mouseover", this.pauseAutoPlay);
    card.removeEventListener("mouseleave", this.startAutoPlay);
  });
    }
  },
  methods: {
    startAutoPlay() {
      this.pauseAutoPlay();
      this.autoPlayInterval = setInterval(() => {
        this.nextCard();
      }, 3500); // Change card every 3.5 seconds
    },
    pauseAutoPlay() {
      if (this.autoPlayInterval) {
        clearInterval(this.autoPlayInterval);
        this.autoPlayInterval = null;
      }
    },
    nextCard() {
      this.activeCardIndex = (this.activeCardIndex + 1) % this.cards.length;
    },
    prevCard() {
      this.activeCardIndex =
        (this.activeCardIndex - 1 + this.cards.length) % this.cards.length;
    },
    goToCard(index) {
      this.activeCardIndex = index;
    },
    getCDNlink(fileKey) {
      let baseUrl = "";
      if (
        process.env.NODE_ENV === "development" ||
        process.env.NODE_ENV === "staging"
      ) {
        baseUrl = config.stagingCDN;
      } else if (process.env.NODE_ENV === "production") {
        baseUrl = config.prodCDN;
      }

      return `${baseUrl}${fileKey}`;
    },
    setupScrollHandling() {
      this.scrollHandler = () => {
        const section = this.$refs.sectionRef;
        const sectionTop = section.offsetTop;
        const scrollY = window.scrollY;
        const viewportHeight = window.innerHeight;

        const scrollStart = sectionTop;
        const scrollEnd = sectionTop + viewportHeight * this.cards.length;

        // Clamp the scroll progress between 0 and 1
        const progress = Math.min(
          1,
          Math.max(0, (scrollY - scrollStart) / (scrollEnd - scrollStart))
        );

        this.scrollProgress = progress;

        const totalCards = this.cards.length;
        const newActiveIndex = Math.min(
          totalCards - 1,
          Math.floor(progress * totalCards)
        );

        if (newActiveIndex !== this.activeCardIndex) {
          this.activeCardIndex = newActiveIndex;
        }

        // Sticky logic: stay sticky until all cards shown
        this.isSticky = scrollY >= scrollStart && scrollY < scrollEnd;
      };

      window.addEventListener("scroll", this.scrollHandler);
    },
  },
};
</script>

<style scoped>
.story-section {
  position: relative;
  width: 100%;
  background-color: #eef7ff;
  padding: 80px 160px;
  overflow: hidden;
}

.story-container {
  width: -webkit-fill-available;
  margin: 0 auto;
  top: 0;
  z-index: 10;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.story-header {
  text-align: center;
  margin-bottom: 60px;
  width: 100%;
}

.story-badge {
  display: inline-block;
  background-color: #ffffff;
  color: #C94374;
  font-weight: 600;
  padding: 8px 20px;
  border-radius: 100px;
  margin-bottom: 20px;
  font-size: 16px;
}

.story-title {
  text-align: center;
  font-family: "Figtree";
  font-size: 48px;
  font-style: normal;
  font-weight: 600;
  line-height: 56px;
  letter-spacing: 0.56px;
  background: var(
    --Section-Title-Gradient,
    linear-gradient(288deg, #046BE1 19%, #224096 64%)
  );
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin: 0;
}

.cards-container {
  position: relative;
  max-width: 1200px;
  margin: 0 auto;
  overflow: hidden;
  padding: 0 20px;
}

.cards-wrapper {
  display: flex;
  transition: transform 0.5s ease-in-out;
  will-change: transform;
}

.carousel-card {
  min-width: 100%;
  padding: 20px;
  box-sizing: border-box;
  opacity: 0;
  transition: opacity 0.5s ease-in-out;
}

.carousel-card.active {
  opacity: 1;
}

.carousel-indicators {
  display: flex;
  justify-content: center;
  margin-top: 30px;
  gap: 10px;
}

.indicator {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: #ccc;
  cursor: pointer;
  transition: all 0.3s ease;
}

.indicator.active {
  background-color: #046BE1;
  transform: scale(1.2);
}

.cards-container {
  display: none; /* Hide the old container */
}

.card-content {
  background: white;
  border-radius: 16px;
  padding: 40px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: row;
  align-items: center;
  height: 100%;
  box-sizing: border-box;
}

.card-left {
  flex: 1;
  padding: 40px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.card-icon {
  width: 80px;
  height: 80px;
  margin-bottom: 24px;
}

.card-text {
  font-size: 18px;
  font-weight: 500;
  font-family: "Figtree";
  text-align: left;
  line-height: 1.6;
  color: #1c1c1c;
  margin: 0;
}

.card-right {
  flex: 1;
  background-color: #a7d1ff;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.card-illustration {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

@media (max-width: 1024px) {
  .story-header {
    margin-bottom: 30px;
  }

  .story-title {
    font-size: 36px;
  }

  .card-content {
    flex-direction: row;
  }

  .card-left {
    padding: 30px;
  }

  .cards-container {
    height: 400px;
  }

  .card-icon {
    margin-bottom: 10px;
    width: 70px;
    height: 70px;
  }

  .success-section {
    background: #fff;
    padding: 0px 80px 80px 80px;
  }
}

@media (max-width: 768px) {
  .story-title {
    font-size: 32px;
    line-height: 35px;
  }

  .story-header {
    margin-bottom: 50px;
  }

  .story-badge {
    font-size: 14px;
    padding: 6px 16px;
    margin-bottom: 10px;
  }

  .responsive-br {
    display: none;
  }

  .card-text {
    font-size: 16px;
    text-align: justify;
  }

  .card-icon {
    width: 60px;
    height: 60px;
    margin-bottom: 16px;
  }

  .cards-container {
    height: 550px;
  }
}

@media (max-width: 480px) {
  .card-content {
    flex-direction: column;
  }
}

@media (max-width: 480px) {
  .story-title {
    font-size: 36px;
  }

  .card-left {
    padding: 24px;
  }

  .card-text {
    font-size: 15px;
  }

  .card-right {
    height: 200px;
  }

  .cards-container {
    height: 450px;
  }
}

@media (max-width: 992px) {
  .card-content {
    flex-direction: column !important;
    text-align: center;
  }

  .card-left,
  .card-right {
    width: 100% !important;
    max-width: 100% !important;
  }

  .card-icon {
    margin: 0 auto 20px !important;
  }

  .card-illustration {
    margin-top: 30px;
    max-height: 200px !important;
    margin-left: auto;
    margin-right: auto;
  }

  .carousel-card {
    padding: 20px 10px;
  }

  .carousel-card .card-content {
    padding: 30px 20px;
  }
}
</style>
